import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertBox } from '~/app/components/common/Alert/AlertBox';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { organizationTabList } from '~/app/components/organization/OrganizationTabList';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { useTranslation } from 'react-i18next';
import { OrganizationPageView } from './OrganizationPageView';

export function OrganizationPageContainer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams<{ orgId: string }>();
  const { t } = useTranslation('organization');
  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization', params.orgId],
    queryFn: () => OrganizationService.findOrganizationById(params.orgId || ''),
    enabled: Boolean(params.orgId),
  });

  let activeTab = 'overview';
  const normalizedPathname = pathname.replace(/\/$/, '');
  for (const tab of organizationTabList) {
    const tabHref = tab.getHref(params.orgId || '').replace(/\/$/, '');
    if (normalizedPathname === tabHref) {
      activeTab = tab.key;
      break;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBox
        title={t('page.errorLoadingOrganizationDetails')}
        description={error.message}
      />
    );
  }

  if (!organization) {
    return <AlertBox message={t('page.noOrganizationFound')} />;
  }

  return (
    <OrganizationPageView
      activeTab={activeTab}
      onBack={() => navigate('/admin/back-office/')}
      onTabChange={(key) => {
        const selectedTab = organizationTabList.find((tab) => tab.key === key);
        if (selectedTab) {
          navigate(selectedTab.getHref(params.orgId || ''));
        }
      }}
      organization={organization}
    />
  );
}
